import React, { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { BusyButton } from "../../components/etams/BusyButton";
import { toast } from "sonner";
import { createJadwal, updateJadwal, apiErrorMessage } from "../../lib/asetApi";
import { label } from "../../hooks/useAsetMeta";
import { hariIniRegion, cariKaryawanRegion, ambilKaryawan } from "./bantuAset";
import PemilihCari from "../../components/etams/PemilihCari";
import { labelKaryawan } from "../../lib/sumberPilihan";

// Form buat/ubah jadwal maintenance (4b-1). Cermin `aset_service.buat_jadwal`
// / `ubah_jadwal` (K107 butir 2: tiga bentuk satu tabel):
//   * TANGGAL   → tanggal_target (sekali jalan)
//   * PERIODE   → interval_nilai + interval_satuan (MINGGU/BULAN) + tanggal_mulai
//                 (bawaan hari ini zona region; boleh dimundurkan bila ingat
//                 servis terakhir — GOLIVE-ASET-SEGARKAN-JADWAL-01)
//   * KILOMETER → interval_km + km_awal (= odometer saat input) + ambang
//                 peringatan (opsional; kosong = bawaan DB). Hanya kendaraan.
//   * peran_notifikasi WAJIB — label jabatan dari meta (S1).
//   * bentuk TERKUNCI saat ubah (bentuk baru = jadwal baru). is_active nol
//     pernah dikirim dari sini (R7) — lewat tombol nonaktif/aktif di Detail.
//   * Saat ubah hanya kolom yang BERUBAH dikirim: periode/jangkar jadwal
//     TERLAMBAT/KM_BASI hanya OWNER (R10) — pesan 403 backend tampil verbatim.

const KOSONG = {
  nama: "", bentuk_jadwal: "", peran_notifikasi: "", tanggal_target: "",
  interval_nilai: "", interval_satuan: "", tanggal_mulai: "",
  interval_km: "", km_awal: "", km_ambang_peringatan: "", pic_employee_id: "", catatan: "",
};
// Bawaan DB `aset_jadwal_maintenance.km_ambang_peringatan` — migration
// `20260915113129_aset_maintenance.sql:273` (`integer NOT NULL DEFAULT 500`).
// Meta nol memuatnya; kalau DEFAULT di DB berubah, angka ini ikut diubah.
const KM_AMBANG_BAWAAN_DB = 500;

export default function JadwalFormDialog({ terbuka, onTutup, onTersimpan, meta, aset, jadwal, kmTerakhir }) {
  const ubah = !!jadwal;
  const [form, setForm] = useState(KOSONG);
  const [menyimpan, setMenyimpan] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const kendaraan = aset?.kategori === "KENDARAAN_BERMOTOR";

  useEffect(() => {
    if (!terbuka) return undefined;
    let hidup = true;
    if (jadwal) {
      setForm({
        nama: jadwal.nama || "", bentuk_jadwal: jadwal.bentuk_jadwal || "",
        peran_notifikasi: jadwal.peran_notifikasi || "", tanggal_target: jadwal.tanggal_target || "",
        interval_nilai: jadwal.interval_nilai ?? "", interval_satuan: jadwal.interval_satuan || "",
        tanggal_mulai: jadwal.tanggal_mulai || "", interval_km: jadwal.interval_km ?? "",
        km_awal: jadwal.km_awal ?? "", km_ambang_peringatan: jadwal.km_ambang_peringatan ?? "",
        pic_employee_id: jadwal.pic_employee_id || "", catatan: jadwal.catatan || "",
      });
    } else {
      setForm({ ...KOSONG, km_awal: kmTerakhir ?? "" });
      hariIniRegion(aset?.region_id).then((h) => { if (hidup && h) upd("tanggal_mulai", h); });
    }
    return () => { hidup = false; };
  }, [terbuka, jadwal, aset, kmTerakhir]);

  const bentuk = form.bentuk_jadwal;
  const angka = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
  const teks = (v) => (typeof v === "string" && v.trim() === "" ? null : v);

  const simpan = async () => {
    if (!form.nama.trim()) { toast.error("Nama jadwal wajib diisi."); return; }
    if (!bentuk) { toast.error("Bentuk jadwal wajib dipilih."); return; }
    if (!form.peran_notifikasi) { toast.error("Jabatan penerima pengingat wajib dipilih."); return; }
    if (bentuk === "TANGGAL" && !form.tanggal_target) { toast.error("Tanggal target wajib diisi."); return; }
    if (bentuk === "PERIODE" && (!form.interval_nilai || !form.interval_satuan)) { toast.error("Interval dan satuannya wajib diisi."); return; }
    if (bentuk === "KILOMETER" && (!form.interval_km || form.km_awal === "")) { toast.error("Interval km dan km saat ini wajib diisi."); return; }
    setMenyimpan(true);
    try {
      const isi = {
        nama: form.nama.trim(),
        peran_notifikasi: form.peran_notifikasi,
        pic_employee_id: teks(form.pic_employee_id),
        catatan: teks(form.catatan.trim()),
      };
      if (bentuk === "TANGGAL") isi.tanggal_target = form.tanggal_target;
      if (bentuk === "PERIODE") {
        isi.interval_nilai = angka(form.interval_nilai);
        isi.interval_satuan = form.interval_satuan;
        isi.tanggal_mulai = teks(form.tanggal_mulai);
      }
      if (bentuk === "KILOMETER") {
        isi.interval_km = angka(form.interval_km);
        isi.km_awal = angka(form.km_awal);
        isi.km_ambang_peringatan = angka(form.km_ambang_peringatan);
      }
      if (ubah) {
        const body = {};
        Object.entries(isi).forEach(([k, v]) => {
          const lama = jadwal[k] === undefined ? null : jadwal[k];
          if ((v ?? null) !== (lama ?? null)) body[k] = v;
        });
        // pic/catatan boleh dikosongkan (null); jangkar nol boleh null di PATCH
        ["tanggal_target", "interval_nilai", "interval_satuan", "tanggal_mulai", "interval_km", "km_awal", "km_ambang_peringatan"]
          .forEach((k) => { if (k in body && body[k] === null) delete body[k]; });
        if (Object.keys(body).length === 0) { toast.message("Tidak ada yang berubah."); onTutup(); return; }
        const r = await updateJadwal(jadwal.id, body);
        toast.success("Jadwal diperbarui.");
        onTersimpan(r.data);
      } else {
        const body = { ...isi, bentuk_jadwal: bentuk };
        Object.keys(body).forEach((k) => { if (body[k] === null) delete body[k]; });
        const r = await createJadwal(aset.id, body);
        toast.success("Jadwal dibuat.");
        onTersimpan(r.data);
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal menyimpan jadwal."));
    } finally {
      setMenyimpan(false);
    }
  };

  const bentukTersedia = (meta?.bentuk_jadwal || []).filter((b) => kendaraan || b.code !== "KILOMETER");

  return (
    <Dialog open={terbuka} onOpenChange={(o) => { if (!o) onTutup(); }}>
      <DialogContent className="max-w-xl" data-testid="jadwal-form-dialog">
        <DialogHeader>
          <DialogTitle>{ubah ? "Ubah Jadwal Maintenance" : "Jadwal Maintenance Baru"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
          <div>
            <Label>Nama Jadwal *</Label>
            <Input value={form.nama} onChange={(e) => upd("nama", e.target.value)} data-testid="jadwal-form-nama" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Bentuk Jadwal *</Label>
              {ubah ? (
                <Input value={label(meta, "bentuk_jadwal", jadwal.bentuk_jadwal)} disabled data-testid="jadwal-form-bentuk-terkunci" />
              ) : (
                <Select value={form.bentuk_jadwal} onValueChange={(v) => upd("bentuk_jadwal", v)}>
                  <SelectTrigger data-testid="jadwal-form-bentuk"><SelectValue placeholder="Pilih bentuk" /></SelectTrigger>
                  <SelectContent>
                    {bentukTersedia.map((b) => <SelectItem key={b.code} value={b.code}>{b.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {ubah && <div className="text-xs text-muted-foreground mt-1">Bentuk tidak bisa diubah. Buat jadwal baru bila bentuknya berbeda.</div>}
            </div>
            <div>
              <Label>Pengingat ke Jabatan *</Label>
              <Select value={form.peran_notifikasi} onValueChange={(v) => upd("peran_notifikasi", v)}>
                <SelectTrigger data-testid="jadwal-form-peran"><SelectValue placeholder="Pilih jabatan" /></SelectTrigger>
                <SelectContent>
                  {(meta?.peran_notifikasi || []).map((p) => <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {bentuk === "TANGGAL" && (
            <div>
              <Label>Tanggal Target *</Label>
              <Input type="date" value={form.tanggal_target} onChange={(e) => upd("tanggal_target", e.target.value)} data-testid="jadwal-form-tanggal-target" />
            </div>
          )}

          {bentuk === "PERIODE" && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>Setiap *</Label>
                <Input type="number" min="1" max="120" value={form.interval_nilai} onChange={(e) => upd("interval_nilai", e.target.value)} data-testid="jadwal-form-interval-nilai" />
              </div>
              <div>
                <Label>Satuan *</Label>
                <Select value={form.interval_satuan} onValueChange={(v) => upd("interval_satuan", v)}>
                  <SelectTrigger data-testid="jadwal-form-interval-satuan"><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>
                    {(meta?.interval_satuan || []).map((s) => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Dihitung Sejak</Label>
                <Input type="date" value={form.tanggal_mulai} onChange={(e) => upd("tanggal_mulai", e.target.value)} data-testid="jadwal-form-tanggal-mulai" />
                {!ubah && <div className="text-xs text-muted-foreground mt-1">Bawaan hari ini. Mundurkan ke tanggal servis terakhir bila ingat.</div>}
              </div>
            </div>
          )}

          {bentuk === "KILOMETER" && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>Setiap (km) *</Label>
                <Input type="number" min="1" value={form.interval_km} onChange={(e) => upd("interval_km", e.target.value)} data-testid="jadwal-form-interval-km" />
              </div>
              <div>
                <Label>Km Saat Ini *</Label>
                <Input type="number" min="0" value={form.km_awal} onChange={(e) => upd("km_awal", e.target.value)} data-testid="jadwal-form-km-awal" />
                {!ubah && <div className="text-xs text-muted-foreground mt-1">Angka odometer saat jadwal ini dibuat.</div>}
              </div>
              <div>
                <Label>Ingatkan saat sisa (km)</Label>
                <Input type="number" min="0" value={form.km_ambang_peringatan} onChange={(e) => upd("km_ambang_peringatan", e.target.value)} data-testid="jadwal-form-km-ambang" />
                {!ubah && <div className="text-xs text-muted-foreground mt-1">Kosong = {KM_AMBANG_BAWAAN_DB} km.</div>}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>PIC Jadwal</Label>
              <PemilihCari value={form.pic_employee_id || null} onChange={(k) => upd("pic_employee_id", k?.id || "")}
                           ambilSatu={ambilKaryawan} cari={cariKaryawanRegion(aset?.region_id)} label={labelKaryawan}
                           tampilAwal bolehKosong placeholder="Ikut PIC aset" placeholderCari="Ketik nama atau kode karyawan…"
                           testId="jadwal-form-pic" />
            </div>
            <div>
              <Label>Catatan</Label>
              <Textarea rows={2} value={form.catatan} onChange={(e) => upd("catatan", e.target.value)} data-testid="jadwal-form-catatan" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onTutup} disabled={menyimpan}>Batal</Button>
          <BusyButton onClick={simpan} busy={menyimpan} busyText="Menyimpan..." data-testid="jadwal-form-simpan">Simpan</BusyButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
